import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Grid,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileDoneOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import api from '../../../api/client';
import CotizacionFinalForm from '../exportacion/cotizacionFinalForm';
import {
  emptyLineaConcepto,
  emptyProducto,
  productosFromApi,
  buildProductosPayload,
  filasResumenDesdeProductos,
  titulosConceptosDesdeFilas,
} from '../cotizaciones/cotizacionFormUtils';
import {
  buildFilasGrillaCotizaciones,
  titulosConceptosDesdeCotizaciones,
  renderCeldaAgrupada,
  renderColumnaNumeroConExpand,
} from '../cotizaciones/cotizacionListaUtils';
import { formatNumeroCotizacion, isNumeroCotizacionValido } from '../cotizaciones/numeroCotizacionUtils';

const { Title, Text, Paragraph } = Typography;

const LIMIT_LISTA = 25;

const slugify = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);

const esConceptoVisibleGrillaImportacion = (nombre) => {
  const n = String(nombre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    (n.includes('honorario') && n.includes('despach')) || n.includes('digitaliz')
  );
};

/**
 * Alineación tipo antd Select pero sin flip vertical (adjustY: false) para que el menú abra siempre hacia abajo.
 */
const selectBuiltinPlacementsBottom = {
  bottomLeft: {
    points: ['tl', 'bl'],
    offset: [0, 4],
    overflow: { adjustX: true, adjustY: false, shiftY: true },
    htmlRegion: 'visible',
    dynamicInset: true,
  },
  bottomRight: {
    points: ['tr', 'br'],
    offset: [0, 4],
    overflow: { adjustX: true, adjustY: false, shiftY: true },
    htmlRegion: 'visible',
    dynamicInset: true,
  },
  topLeft: {
    points: ['bl', 'tl'],
    offset: [0, -4],
    overflow: { adjustX: true, adjustY: false, shiftY: true },
    htmlRegion: 'visible',
    dynamicInset: true,
  },
  topRight: {
    points: ['br', 'tr'],
    offset: [0, -4],
    overflow: { adjustX: true, adjustY: false, shiftY: true },
    htmlRegion: 'visible',
    dynamicInset: true,
  },
};

/** Evita recorte por Card/overflow; fuerza apertura inferior. */
const selectPopupProps = (isNarrow) => ({
  getPopupContainer: () => document.body,
  placement: 'bottomLeft',
  listHeight: isNarrow ? 280 : undefined,
  builtinPlacements: selectBuiltinPlacementsBottom,
});

const Importacion = () => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [modo, setModo] = useState('lista');

  const [loadingLista, setLoadingLista] = useState(true);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [pageLista, setPageLista] = useState(1);
  const [totalLista, setTotalLista] = useState(0);
  const [searchLista, setSearchLista] = useState('');
  const [searchListaDebounced, setSearchListaDebounced] = useState('');

  const [loadingCat, setLoadingCat] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [conceptosCatalogo, setConceptosCatalogo] = useState([]);

  const [numeroCliente, setNumeroCliente] = useState(null);
  const [esClientePotencial, setEsClientePotencial] = useState(false);
  const [nombreClientePotencial, setNombreClientePotencial] = useState('');
  const [productosBloques, setProductosBloques] = useState([emptyProducto()]);
  const [aprobada, setAprobada] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cotizacionIdEditando, setCotizacionIdEditando] = useState(null);
  const [cotizacionNroEditando, setCotizacionNroEditando] = useState(null);
  const [numeroSecuencialForm, setNumeroSecuencialForm] = useState(null);

  const [modalVerOpen, setModalVerOpen] = useState(false);
  const [detalleVer, setDetalleVer] = useState(null);
  const [loadingVer, setLoadingVer] = useState(false);
  const [loadingPdfVer, setLoadingPdfVer] = useState(false);

  const [cotIdLiquidacion, setCotIdLiquidacion] = useState(null);
  const [cotizacionesExpandidas, setCotizacionesExpandidas] = useState(() => new Set());

  const clienteSeleccionado = useMemo(
    () => clientes.find((c) => c.numeroCliente === numeroCliente) || null,
    [clientes, numeroCliente]
  );

  const clienteParaResumen = useMemo(() => {
    if (esClientePotencial && nombreClientePotencial.trim()) {
      return { nombre: nombreClientePotencial.trim(), cuit: '—' };
    }
    return clienteSeleccionado;
  }, [esClientePotencial, nombreClientePotencial, clienteSeleccionado]);

  const toggleExpandCotizacion = useCallback((id) => {
    setCotizacionesExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const filasLista = useMemo(
    () => buildFilasGrillaCotizaciones(cotizaciones, cotizacionesExpandidas),
    [cotizaciones, cotizacionesExpandidas]
  );

  const cargarLista = useCallback(async () => {
    setLoadingLista(true);
    try {
      const { data } = await api.get('/cotizaciones', {
        params: {
          page: pageLista,
          limit: LIMIT_LISTA,
          search: searchListaDebounced.trim() || undefined,
          tipoOperacion: 'IMPORTACION',
        },
      });
      setCotizaciones(data.items || []);
      setTotalLista(data.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar cotizaciones');
    } finally {
      setLoadingLista(false);
    }
  }, [pageLista, searchListaDebounced]);

  useEffect(() => {
    const t = setTimeout(() => setSearchListaDebounced(searchLista.trim()), 350);
    return () => clearTimeout(t);
  }, [searchLista]);

  useEffect(() => {
    if (modo === 'lista') {
      cargarLista();
    }
  }, [modo, cargarLista]);

  const cargarCatalogos = useCallback(async () => {
    setLoadingCat(true);
    try {
      const [rCli, rProd, rCon] = await Promise.all([
        api.get('/clientes', { params: { page: 1, limit: 500, soloActivos: true } }),
        api.get('/productos', { params: { page: 1, limit: 500 } }),
        api.get('/conceptos', { params: { page: 1, limit: 500 } }),
      ]);
      setClientes(rCli.data?.items || []);
      setProductos(rProd.data?.items || []);
      setConceptosCatalogo(rCon.data?.items || []);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoadingCat(false);
    }
  }, []);

  const abrirNuevaCotizacion = async () => {
    setModo('formulario');
    setCotizacionIdEditando(null);
    setCotizacionNroEditando(null);
    setNumeroSecuencialForm(null);
    setNumeroCliente(null);
    setEsClientePotencial(false);
    setNombreClientePotencial('');
    setProductosBloques([emptyProducto()]);
    setAprobada(false);
    setObservaciones('');
    setLoadingCat(true);
    try {
      const tasks = [api.get('/cotizaciones/siguiente-numero', { params: { tipoOperacion: 'IMPORTACION' } })];
      if (!clientes.length) {
        tasks.push(
          api.get('/clientes', { params: { page: 1, limit: 500, soloActivos: true } }),
          api.get('/productos', { params: { page: 1, limit: 500 } }),
          api.get('/conceptos', { params: { page: 1, limit: 500 } })
        );
      }
      const results = await Promise.all(tasks);
      setNumeroSecuencialForm(formatNumeroCotizacion(results[0].data?.siguiente ?? 1));
      if (results[1]) {
        setClientes(results[1].data?.items || []);
        setProductos(results[2].data?.items || []);
        setConceptosCatalogo(results[3].data?.items || []);
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al preparar la cotizacion');
      setNumeroSecuencialForm('001');
    } finally {
      setLoadingCat(false);
    }
  };

  const abrirVer = useCallback(async (row) => {
    setModalVerOpen(true);
    setDetalleVer(null);
    setLoadingVer(true);
    try {
      const { data } = await api.get(`/cotizaciones/${row.id}`);
      setDetalleVer(data);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar la cotizacion');
      setModalVerOpen(false);
    } finally {
      setLoadingVer(false);
    }
  }, []);

  const cerrarVer = useCallback(() => {
    setModalVerOpen(false);
    setDetalleVer(null);
  }, []);

  const descargarPdfVer = useCallback(async () => {
    if (!detalleVer?.id) return;
    setLoadingPdfVer(true);
    try {
      const { data } = await api.get(`/cotizaciones/${detalleVer.id}/resumen-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const nro = detalleVer.numeroSecuencial ?? detalleVer.id;
      const clienteSlug = slugify(detalleVer.clienteNombre || 'cliente');
      a.download = `cotizacion-impo-${nro}-${clienteSlug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Error al generar el PDF';
      try {
        const d = e?.response?.data;
        if (d instanceof Blob) {
          const txt = await d.text();
          const j = JSON.parse(txt);
          if (j?.message) msg = j.message;
        } else if (typeof d?.message === 'string') {
          msg = d.message;
        }
      } catch {
        /* ignore */
      }
      message.error(msg);
    } finally {
      setLoadingPdfVer(false);
    }
  }, [detalleVer]);

  const abrirLiquidacion = useCallback((row) => {
    if (!row.aprobada) return;
    setCotIdLiquidacion(row.id);
    setModo('cotizacion_final');
  }, []);

  const cerrarLiquidacion = useCallback(() => {
    setCotIdLiquidacion(null);
    setModo('lista');
    cargarLista();
  }, [cargarLista]);

  const eliminarCotizacion = useCallback(
    async (row) => {
      try {
        await api.delete(`/cotizaciones/${row.id}`);
        message.success('Cotizacion eliminada');
        cargarLista();
      } catch (e) {
        message.error(e?.response?.data?.message || 'No se pudo eliminar la cotizacion');
      }
    },
    [cargarLista]
  );

  const toggleActiva = useCallback(
    async (row, checked) => {
      try {
        await api.patch(`/cotizaciones/${row.id}/activa`, { activa: checked });
        message.success(checked ? 'Cotizacion activada' : 'Cotizacion desactivada');
        cargarLista();
      } catch (e) {
        message.error(e?.response?.data?.message || 'No se pudo cambiar el estado');
      }
    },
    [cargarLista]
  );

  const abrirEditar = useCallback(async (row) => {
    setModo('formulario');
    setCotizacionIdEditando(row.id);
    setCotizacionNroEditando(formatNumeroCotizacion(row.numeroSecuencial ?? row.id));
    setNumeroSecuencialForm(formatNumeroCotizacion(row.numeroSecuencial ?? row.id));
    setLoadingCat(true);
    try {
      const [rCli, rProd, rCon, cotRes] = await Promise.all([
        api.get('/clientes', { params: { page: 1, limit: 500, soloActivos: true } }),
        api.get('/productos', { params: { page: 1, limit: 500 } }),
        api.get('/conceptos', { params: { page: 1, limit: 500 } }),
        api.get(`/cotizaciones/${row.id}`),
      ]);
      setClientes(rCli.data?.items || []);
      setProductos(rProd.data?.items || []);
      setConceptosCatalogo(rCon.data?.items || []);
      const data = cotRes.data;
      if (data.eliminada) {
        message.warning('Esta cotizacion fue eliminada');
        setModo('lista');
        setCotizacionIdEditando(null);
        setCotizacionNroEditando(null);
        setNumeroSecuencialForm(null);
        return;
      }
      if (data.activa === false) {
        message.warning('Activa la cotizacion desde la grilla antes de editarla');
        setModo('lista');
        setCotizacionIdEditando(null);
        setCotizacionNroEditando(null);
        setNumeroSecuencialForm(null);
        return;
      }
      if (
        !data.esClientePotencial &&
        data.numeroCliente != null &&
        !(rCli.data?.items || []).some((c) => c.numeroCliente === data.numeroCliente)
      ) {
        try {
          const { data: cliInactivo } = await api.get(`/clientes/${data.numeroCliente}`);
          setClientes((prev) => [...prev, cliInactivo]);
        } catch {
          /* ignore */
        }
      }
      setCotizacionNroEditando(formatNumeroCotizacion(data.numeroSecuencial ?? data.id));
      setNumeroSecuencialForm(formatNumeroCotizacion(data.numeroSecuencial ?? data.id));
      setEsClientePotencial(!!data.esClientePotencial);
      setNombreClientePotencial(
        data.esClientePotencial ? data.clientePotencialNombre || data.clienteNombre || '' : ''
      );
      setNumeroCliente(data.esClientePotencial ? null : data.numeroCliente);
      setAprobada(!!data.aprobada);
      setObservaciones(data.observaciones != null ? String(data.observaciones) : '');
      const bloques = productosFromApi(data).map((b) => ({
        ...b,
        lineasConcepto: b.lineasConcepto.length ? b.lineasConcepto : [emptyLineaConcepto()],
      }));
      setProductosBloques(bloques.length ? bloques : [emptyProducto()]);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar la cotizacion');
      setModo('lista');
      setCotizacionIdEditando(null);
      setCotizacionNroEditando(null);
      setNumeroSecuencialForm(null);
    } finally {
      setLoadingCat(false);
    }
  }, []);

  const volverALista = () => {
    setCotizacionIdEditando(null);
    setCotizacionNroEditando(null);
    setNumeroSecuencialForm(null);
    setModo('lista');
  };

  const guardarNumeroSecuencialLista = useCallback(
    async (row, nuevoNumero) => {
      try {
        await api.patch(`/cotizaciones/${row.id}/numero-secuencial`, {
          numeroSecuencial: nuevoNumero,
        });
        message.success(`Numero actualizado a ${nuevoNumero}`);
        cargarLista();
      } catch (e) {
        message.error(e?.response?.data?.message || 'No se pudo actualizar el numero');
        cargarLista();
      }
    },
    [cargarLista]
  );

  const agregarProducto = () => {
    setProductosBloques((prev) => [...prev, emptyProducto()]);
  };

  const quitarProducto = (bloqueKey) => {
    setProductosBloques((prev) =>
      prev.length <= 1 ? prev : prev.filter((b) => b.key !== bloqueKey)
    );
  };

  const actualizarProducto = (bloqueKey, patch) => {
    setProductosBloques((prev) =>
      prev.map((b) => (b.key === bloqueKey ? { ...b, ...patch } : b))
    );
  };

  const agregarLineaConcepto = (bloqueKey) => {
    setProductosBloques((prev) =>
      prev.map((b) =>
        b.key === bloqueKey
          ? { ...b, lineasConcepto: [...b.lineasConcepto, emptyLineaConcepto()] }
          : b
      )
    );
  };

  const quitarLinea = (bloqueKey, lineKey) => {
    setProductosBloques((prev) =>
      prev.map((b) => {
        if (b.key !== bloqueKey) return b;
        if (b.lineasConcepto.length <= 1) return b;
        return { ...b, lineasConcepto: b.lineasConcepto.filter((l) => l.key !== lineKey) };
      })
    );
  };

  const actualizarLinea = (bloqueKey, lineKey, patch) => {
    setProductosBloques((prev) =>
      prev.map((b) =>
        b.key === bloqueKey
          ? {
              ...b,
              lineasConcepto: b.lineasConcepto.map((l) =>
                l.key === lineKey ? { ...l, ...patch } : l
              ),
            }
          : b
      )
    );
  };

  const onConceptoChange = (bloqueKey, lineKey, conceptoId) => {
    const c = conceptosCatalogo.find((x) => x.id === conceptoId);
    actualizarLinea(bloqueKey, lineKey, {
      conceptoId,
      conceptoNombre: c?.nombre || '',
    });
  };

  const guardarCotizacion = async () => {
    if (esClientePotencial) {
      if (!nombreClientePotencial.trim()) {
        message.warning('Ingresa el nombre del cliente potencial');
        return;
      }
    } else if (numeroCliente == null) {
      message.warning('Selecciona un cliente');
      return;
    }
    if (!isNumeroCotizacionValido(numeroSecuencialForm)) {
      message.warning('Indica un numero de cotizacion valido (ej: 001, 023)');
      return;
    }
    const built = buildProductosPayload(productosBloques);
    if (built.error) {
      message.warning(built.error);
      return;
    }

    setGuardando(true);
    try {
      const payload = {
        ...(esClientePotencial
          ? { clientePotencialNombre: nombreClientePotencial.trim() }
          : { numeroCliente }),
        productos: built.productos,
        aprobada,
        observaciones: observaciones.trim() || undefined,
        tipoOperacion: 'IMPORTACION',
        numeroSecuencial: formatNumeroCotizacion(numeroSecuencialForm),
      };
      if (cotizacionIdEditando != null) {
        const { data } = await api.put(`/cotizaciones/${cotizacionIdEditando}`, payload);
        message.success(`Cotizacion Nº ${data.numeroSecuencial ?? data.id} actualizada`);
      } else {
        const { data } = await api.post('/cotizaciones', payload);
        message.success(`Cotizacion guardada (Nº ${data.numeroSecuencial ?? data.id})`);
      }
      setCotizacionIdEditando(null);
      setCotizacionNroEditando(null);
      setNumeroSecuencialForm(null);
      setAprobada(false);
      setObservaciones('');
      setNumeroCliente(null);
      setEsClientePotencial(false);
      setNombreClientePotencial('');
      setProductosBloques([emptyProducto()]);
      setPageLista(1);
      setModo('lista');
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al guardar cotizacion');
    } finally {
      setGuardando(false);
    }
  };

  const datosGrilla = useMemo(
    () =>
      filasResumenDesdeProductos(
        clienteParaResumen,
        productosBloques,
        productos,
        conceptosCatalogo,
        aprobada
      ),
    [clienteParaResumen, productosBloques, productos, conceptosCatalogo, aprobada]
  );

  const titulosConceptosResumen = useMemo(() => {
    const nombres = new Set();
    productosBloques.forEach((bloque) => {
      (bloque.lineasConcepto || []).forEach((line) => {
        if (!line.conceptoId) return;
        const c = conceptosCatalogo.find((x) => x.id === line.conceptoId);
        if (c && c.muestraEnGrilla !== false && line.conceptoNombre) {
          nombres.add(line.conceptoNombre);
        }
      });
    });
    return Array.from(nombres);
  }, [productosBloques, conceptosCatalogo]);

  const columnasGrilla = useMemo(() => {
    const base = [
      { title: 'CUIT', dataIndex: 'cuit', width: 130, fixed: isNarrow ? 'left' : undefined },
      {
        title: 'Nombre del cliente',
        dataIndex: 'nombreCliente',
        ellipsis: true,
        minWidth: 160,
      },
      {
        title: 'Descripcion',
        dataIndex: 'posicionArancelaria',
        ellipsis: true,
        width: 160,
      },
      {
        title: 'Nombre del producto o servicio',
        dataIndex: 'nombreProducto',
        ellipsis: true,
        minWidth: 180,
      },
    ];
    const dinamicas = titulosConceptosResumen.map((nombre) => ({
      title: nombre,
      key: `resumen-${nombre}`,
      ellipsis: true,
      minWidth: 160,
      render: (_, row) => {
        const hitKey = Object.keys(row).find(
          (k) => k.startsWith('concepto_nombre_') && row[k] === nombre
        );
        if (!hitKey) return '—';
        const dataKey = hitKey.replace('concepto_nombre_', 'concepto_');
        return row[dataKey] || '—';
      },
    }));
    const colAprobada = {
      title: 'Aprobada',
      dataIndex: 'aprobada',
      width: 100,
      align: 'center',
      render: (v) => (v ? <Tag color="green">Si</Tag> : <Tag>No</Tag>),
    };
    return [...base, ...dinamicas, colAprobada];
  }, [titulosConceptosResumen, isNarrow]);

  const titulosConceptosGrillaLista = useMemo(
    () => titulosConceptosDesdeCotizaciones(cotizaciones),
    [cotizaciones]
  );

  const titulosConceptosGrillaListaVisibles = useMemo(
    () => titulosConceptosGrillaLista.filter(esConceptoVisibleGrillaImportacion),
    [titulosConceptosGrillaLista]
  );

  const columnasLista = useMemo(() => {
    const fijas = [
      {
        title: 'Nº',
        dataIndex: 'numeroSecuencial',
        width: 110,
        responsive: ['sm'],
        render: (v, row) =>
          renderColumnaNumeroConExpand(v, row, toggleExpandCotizacion, guardarNumeroSecuencialLista),
      },
      {
        title: 'Nombre del cliente',
        dataIndex: 'clienteNombre',
        ellipsis: true,
        width: 160,
        render: (v, row) => renderCeldaAgrupada(row, v || '—'),
      },
      {
        title: 'Descripcion',
        dataIndex: 'posicionArancelaria',
        ellipsis: true,
        width: 160,
      },
      {
        title: 'Nombre del producto o servicio',
        dataIndex: 'productoNombre',
        ellipsis: true,
        minWidth: 180,
      },
    ];
    const colsConceptos = titulosConceptosGrillaListaVisibles.map((nombreConcepto) => ({
      title: nombreConcepto,
      key: `cg-${nombreConcepto}`,
      ellipsis: true,
      minWidth: 140,
      render: (_, row) => {
        const hit = (row.conceptosGrilla || []).find((l) => l.nombre === nombreConcepto);
        const d = hit?.detalle;
        return d != null && String(d).trim() ? String(d).trim() : '—';
      },
    }));
    const colAprobada = {
      title: 'Aprobada',
      dataIndex: 'aprobada',
      width: 72,
      align: 'center',
      render: (v, row) =>
        renderCeldaAgrupada(row, v ? <Tag color="green">Si</Tag> : <Tag>No</Tag>),
    };
    const colActiva = {
      title: 'Activa',
      key: 'activa',
      width: 92,
      align: 'center',
      render: (_, row) =>
        renderCeldaAgrupada(
          row,
          <Switch
            size="small"
            checked={row.activa !== false}
            onChange={(checked) => void toggleActiva(row, checked)}
            checkedChildren="Si"
            unCheckedChildren="No"
          />
        ),
    };
    const colLiquidacion = {
      title: 'Cot. final',
      key: 'liquidacion',
      width: 130,
      align: 'center',
      render: (_, row) => {
        const inactiva = row.activa === false;
        return renderCeldaAgrupada(
          row,
          row.aprobada ? (
            <Button
              type="primary"
              size="small"
              ghost
              icon={<FileDoneOutlined />}
              disabled={inactiva}
              onClick={() => abrirLiquidacion(row)}
            >
              {row.tieneCotizacionFinal ? 'Editar' : 'Cargar'}
            </Button>
          ) : (
            <Text type="secondary">—</Text>
          )
        );
      },
    };
    const colAcciones = {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 108,
      render: (_, row) => {
        const inactiva = row.activa === false;
        return renderCeldaAgrupada(
          row,
          <Space size={0} wrap>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              title="Ver"
              disabled={inactiva}
              onClick={() => void abrirVer(row)}
            />
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              title="Editar"
              disabled={inactiva}
              onClick={() => void abrirEditar(row)}
            />
            {!row.aprobada ? (
              inactiva ? (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  title="Eliminar"
                  disabled
                />
              ) : (
                <Popconfirm
                  title="Eliminar cotizacion"
                  description="Quedara oculta en esta grilla; podras verla en Cotizaciones eliminadas."
                  okText="Eliminar"
                  cancelText="Cancelar"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => void eliminarCotizacion(row)}
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    title="Eliminar"
                  />
                </Popconfirm>
              )
            ) : null}
          </Space>
        );
      },
    };
    return [...fijas, ...colsConceptos, colAprobada, colActiva, colLiquidacion, colAcciones];
  }, [
    titulosConceptosGrillaListaVisibles,
    toggleExpandCotizacion,
    guardarNumeroSecuencialLista,
    abrirVer,
    abrirEditar,
    abrirLiquidacion,
    eliminarCotizacion,
    toggleActiva,
  ]);

  const scrollXLista = useMemo(
    () => Math.max(1000, 540 + titulosConceptosGrillaListaVisibles.length * 140),
    [titulosConceptosGrillaListaVisibles.length]
  );

  if (modo === 'cotizacion_final' && cotIdLiquidacion != null) {
    return (
      <CotizacionFinalForm
        cotId={cotIdLiquidacion}
        onBack={cerrarLiquidacion}
        onSaved={cargarLista}
        defaultTipoOperacion="IMPORTACION"
        serviciosAcordadosDisabled
      />
    );
  }

  if (modo === 'lista') {
    return (
      <>
      <Card
        title={
          <Title level={5} style={{ margin: 0, color: '#198039' }}>
            Importación — Cotizaciones
          </Title>
        }
        style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
        styles={{ body: { padding: isNarrow ? 12 : 16 } }}
        extra={
          <Space wrap size="middle" style={{ justifyContent: 'flex-end' }}>
            <Input.Search
              allowClear
              placeholder="Buscar por Nº, cliente, CUIT, producto, conceptos..."
              value={searchLista}
              onChange={(e) => {
                setSearchLista(e.target.value);
                setPageLista(1);
              }}
              style={{ width: isNarrow ? '100%' : 320 }}
            />
            <Button type="primary" onClick={abrirNuevaCotizacion}>
              Nueva cotizacion
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="key"
          loading={loadingLista}
          columns={columnasLista}
          dataSource={filasLista}
          onRow={(record) => ({
            style:
              record.activa === false
                ? { background: '#f3f4f6', color: '#9ca3af' }
                : record._esFilasProductoSecundario
                  ? { background: '#fafafa' }
                  : undefined,
          })}
          pagination={{
            current: pageLista,
            pageSize: LIMIT_LISTA,
            total: totalLista,
            showSizeChanger: false,
            onChange: (p) => setPageLista(p),
            showTotal: (t) => `${t} cotizaciones`,
            size: isNarrow ? 'small' : 'default',
            simple: isNarrow,
          }}
          scroll={{ x: scrollXLista }}
          size="small"
        />
      </Card>

      <Modal
        title={detalleVer ? `Cotizacion Nº ${detalleVer.numeroSecuencial ?? detalleVer.id}` : 'Detalle'}
        open={modalVerOpen}
        onCancel={cerrarVer}
        footer={[
          <Button
            key="pdf"
            icon={<FilePdfOutlined />}
            loading={loadingPdfVer}
            disabled={!detalleVer || loadingVer}
            onClick={() => void descargarPdfVer()}
          >
            PDF
          </Button>,
          <Button key="cerrar" type="primary" onClick={cerrarVer}>
            Cerrar
          </Button>,
        ]}
        width={isNarrow ? 'calc(100vw - 24px)' : 560}
        destroyOnClose
      >
        <Spin spinning={loadingVer}>
          {detalleVer && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Cliente
                </Text>
                <div>
                  {detalleVer.clienteNombre || '—'}
                  {!detalleVer.esClientePotencial && detalleVer.cuit ? ` · CUIT ${detalleVer.cuit}` : ''}
                </div>
                {detalleVer.esClientePotencial ? (
                  <Text type="secondary">Cliente potencial</Text>
                ) : (
                  <Text type="secondary">Nº cliente: {detalleVer.numeroCliente ?? '—'}</Text>
                )}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {(detalleVer.productos || []).length > 1 ? 'Productos o Servicios' : 'Producto o Servicio'}
                </Text>
                {(detalleVer.productos || []).length > 0 ? (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {(detalleVer.productos || []).map((p) => (
                      <div key={p.id ?? p.productoId}>
                        <Text strong>{p.productoNombre || '—'}</Text>
                        {p.posicionArancelaria ? (
                          <div>
                            <Text type="secondary">Descripcion: {p.posicionArancelaria}</Text>
                          </div>
                        ) : null}
                        {(p.conceptos || []).length > 0 ? (
                          <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 8 }}>
                            {(p.conceptos || []).map((c) => (
                              <div key={c.id}>
                                <Text type="secondary">{c.nombre || 'Concepto'}</Text>
                                <Paragraph style={{ marginBottom: 0, marginTop: 2, whiteSpace: 'pre-wrap' }}>
                                  {c.detalle != null && String(c.detalle).trim() ? c.detalle : '—'}
                                </Paragraph>
                              </div>
                            ))}
                          </Space>
                        ) : (
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            Sin conceptos
                          </Text>
                        )}
                      </div>
                    ))}
                  </Space>
                ) : (
                  <>
                    <div>{detalleVer.productoNombre || '—'}</div>
                    {detalleVer.posicionArancelaria ? (
                      <Text type="secondary">Descripcion: {detalleVer.posicionArancelaria}</Text>
                    ) : null}
                  </>
                )}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Observaciones
                </Text>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {detalleVer.observaciones != null && String(detalleVer.observaciones).trim()
                    ? detalleVer.observaciones
                    : '—'}
                </Paragraph>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ marginRight: 8 }}>
                  Aprobada
                </Text>
                {detalleVer.aprobada ? <Tag color="green">Si</Tag> : <Tag>No</Tag>}
              </div>
            </Space>
          )}
        </Spin>
      </Modal>
      </>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={volverALista}>
              Volver
            </Button>
            <Title level={5} style={{ margin: 0, color: '#198039' }}>
              {cotizacionIdEditando != null
                ? `Editar cotizacion Nº ${cotizacionNroEditando ?? cotizacionIdEditando}`
                : 'Nueva cotizacion'}
            </Title>
          </Space>
        }
        style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
        styles={{ body: { padding: isNarrow ? 12 : 16 } }}
        loading={loadingCat}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Elegi el cliente. Luego agrega uno o mas productos; en cada uno, los conceptos y su detalle.
        </Text>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[16, 16]} align="stretch">
            <Col xs={24} sm={8} md={6} lg={4}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>
                Nº cotización
              </Text>
              <Input
                style={{ width: '100%' }}
                value={numeroSecuencialForm ?? ''}
                maxLength={20}
                onChange={(e) => setNumeroSecuencialForm(e.target.value.replace(/[^\d]/g, ''))}
                onBlur={() => {
                  if (isNumeroCotizacionValido(numeroSecuencialForm)) {
                    setNumeroSecuencialForm(formatNumeroCotizacion(numeroSecuencialForm));
                  }
                }}
                placeholder="001"
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Con ceros (ej: 001). Correlativo de importación
              </Text>
            </Col>
            <Col xs={24} sm={16} md={10} lg={10}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  Cliente
                </Text>
                <Select
                  showSearch
                  allowClear
                  disabled={esClientePotencial}
                  placeholder="Seleccionar cliente"
                  style={{ width: '100%' }}
                  optionFilterProp="label"
                  value={numeroCliente ?? undefined}
                  onChange={(v) => setNumeroCliente(v ?? null)}
                  options={clientes.map((c) => ({
                    value: c.numeroCliente,
                    label: `${c.nombre} (Nº ${c.numeroCliente})`,
                  }))}
                  {...selectPopupProps(isNarrow)}
                />
                <div style={{ marginTop: 10 }}>
                  <Checkbox
                    checked={esClientePotencial}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEsClientePotencial(checked);
                      if (checked) {
                        setNumeroCliente(null);
                      } else {
                        setNombreClientePotencial('');
                      }
                    }}
                  >
                    Cliente potencial
                  </Checkbox>
                </div>
                {esClientePotencial ? (
                  <Input
                    style={{ marginTop: 8 }}
                    placeholder="Nombre del cliente potencial"
                    value={nombreClientePotencial}
                    onChange={(e) => setNombreClientePotencial(e.target.value)}
                  />
                ) : null}
              </div>
            </Col>
            <Col xs={24} md={8} lg={10}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>
                Observaciones
              </Text>
              <Input.TextArea
                rows={isNarrow ? 2 : 5}
                placeholder="Notas u observaciones sobre esta cotización..."
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </Col>
          </Row>

          <div>
            <Space align="center" wrap>
              <Text strong>Cotizacion aprobada</Text>
              <Switch checked={aprobada} onChange={setAprobada} checkedChildren="Si" unCheckedChildren="No" />
            </Space>
          </div>

          <div>
            <Space align="center" wrap style={{ marginBottom: 12 }}>
              <Text strong>Productos</Text>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={agregarProducto}>
                Agregar producto
              </Button>
            </Space>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {productosBloques.map((bloque, bloqueIndex) => (
                <Card
                  key={bloque.key}
                  size="small"
                  title={`Producto ${bloqueIndex + 1}`}
                  extra={
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => quitarProducto(bloque.key)}
                      disabled={productosBloques.length <= 1}
                      aria-label="Quitar producto"
                    />
                  }
                  styles={{ body: { padding: isNarrow ? 10 : 12 } }}
                  style={{ borderColor: '#d8e8dc' }}
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: 6 }}>
                        Producto
                      </Text>
                      <Select
                        showSearch
                        allowClear
                        placeholder="Seleccionar producto"
                        style={{ width: '100%' }}
                        optionFilterProp="label"
                        value={bloque.productoId ?? undefined}
                        onChange={(v) => actualizarProducto(bloque.key, { productoId: v ?? null })}
                        options={productos.map((p) => ({
                          value: p.id,
                          label: `${p.nombreProducto}${p.posicionArancelaria ? ` — ${p.posicionArancelaria}` : ''}`,
                        }))}
                        {...selectPopupProps(isNarrow)}
                      />
                    </div>
                    <div>
                      <Space align="center" wrap style={{ marginBottom: 8 }}>
                        <Text strong>Conceptos</Text>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => agregarLineaConcepto(bloque.key)}
                        >
                          Agregar concepto
                        </Button>
                      </Space>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {bloque.lineasConcepto.map((line, index) => (
                          <Card
                            key={line.key}
                            size="small"
                            type="inner"
                            styles={{ body: { padding: isNarrow ? 10 : 12 } }}
                            style={{ borderColor: '#e8f0ea' }}
                          >
                            <Space
                              direction={isNarrow ? 'vertical' : 'horizontal'}
                              style={{ width: '100%' }}
                              align={isNarrow ? 'stretch' : 'start'}
                              wrap
                            >
                              <div
                                style={{
                                  flex: isNarrow ? 1 : '0 0 min(100%, 520px)',
                                  minWidth: isNarrow ? 0 : 400,
                                  width: '100%',
                                  maxWidth: '100%',
                                }}
                              >
                                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 4 }}>
                                  Concepto {index + 1}
                                </Text>
                                <Select
                                  showSearch
                                  allowClear
                                  placeholder="Elegir concepto"
                                  style={{ width: '100%', minWidth: isNarrow ? undefined : 400 }}
                                  optionFilterProp="label"
                                  popupMatchSelectWidth={isNarrow}
                                  dropdownStyle={
                                    isNarrow
                                      ? { maxWidth: 'calc(100vw - 24px)', minWidth: 0 }
                                      : { minWidth: 480, maxWidth: 720 }
                                  }
                                  value={line.conceptoId ?? undefined}
                                  onChange={(id) => onConceptoChange(bloque.key, line.key, id)}
                                  options={conceptosCatalogo.map((c) => ({
                                    value: c.id,
                                    label: c.nombre,
                                  }))}
                                  {...selectPopupProps(isNarrow)}
                                />
                              </div>
                              <div
                                style={{
                                  flex: isNarrow ? 1 : '1 1 480px',
                                  minWidth: isNarrow ? 0 : 420,
                                  width: '100%',
                                  maxWidth: '100%',
                                }}
                              >
                                <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 4 }}>
                                  Detalle / lo que cuenta
                                </Text>
                                <Input.TextArea
                                  rows={2}
                                  placeholder="Ej: Honorarios despachante, importes, observaciones..."
                                  value={line.detalle}
                                  onChange={(e) =>
                                    actualizarLinea(bloque.key, line.key, { detalle: e.target.value })
                                  }
                                  style={{ width: '100%', minWidth: isNarrow ? undefined : 420 }}
                                />
                              </div>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => quitarLinea(bloque.key, line.key)}
                                disabled={bloque.lineasConcepto.length <= 1}
                                aria-label="Quitar concepto"
                              />
                            </Space>
                          </Card>
                        ))}
                      </Space>
                    </div>
                  </Space>
                </Card>
              ))}
            </Space>
          </div>

          <div>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={guardando}
              onClick={guardarCotizacion}
              size="large"
            >
              {cotizacionIdEditando != null ? 'Guardar cambios' : 'Guardar cotizacion'}
            </Button>
          </div>
        </Space>
      </Card>

      <Card
        title="Resumen"
        style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
        styles={{ body: { padding: isNarrow ? 8 : 12 } }}
      >
        {!clienteSeleccionado || !productosBloques.some((b) => b.productoId != null) ? (
          <Text type="secondary">Selecciona cliente y al menos un producto o servicio para ver la grilla.</Text>
        ) : (
          <Table
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={columnasGrilla}
            dataSource={datosGrilla}
            rowKey="key"
          />
        )}
      </Card>
    </Space>
  );
};

export default Importacion;
